/**
 * The runner interface — the only new idea in the domain-pack format.
 *
 * A pack picks a runner; the engine does not care how code executes, only that
 * the work product and the result reach the tutor. The vocabulary here matches
 * the backend's `EditorRun` exactly, so a run travels from the browser to the
 * prompt without translation.
 */

/**
 * `ok`      — it ran and produced output
 * `silent`  — it ran, produced nothing (a real teaching moment: "nothing printed")
 * `error`   — a traceback, an evaluation error, or a timeout
 * `played`  — Strudel: the pattern is audible. There is no stdout to show.
 * `drawing` — p5: a canvas is up and drawing frames. Like `played`, the result is
 *             something only the learner can see, so the tutor must ask.
 */
export type RunOutcome = "ok" | "silent" | "error" | "played" | "drawing";

export interface RunResult {
  outcome: RunOutcome;
  /** stdout, a traceback, or a one-line description of what happened. */
  detail: string;
  /** Set when the runner produced something visual rather than textual. */
  artifact?: { kind: "image"; mime: string; base64: string };
}

/** What the editor panel asks the learner for input with. */
export type StdinProvider = (prompt: string) => string | null;

export interface Runner {
  /** Matches `pack.json`'s `runner` field. */
  readonly id: string;
  /** CodeMirror language mode, and the `language` tag on the snapshot. */
  readonly language: string;
  /** Human label for the run button, e.g. "Run" or "Play". */
  readonly runLabel: string;
  /** Shown in an empty editor so the first sixty seconds can do something. */
  readonly starterCode: string;
  /** True when the runner keeps making sound/output until stopped. */
  readonly canStop: boolean;
  /**
   * What the panel calls that state while it lasts: "playing" for sound, "running"
   * for a sketch. Only meaningful alongside `canStop`.
   */
  readonly liveLabel?: string;

  /** Idempotent; safe to call more than once. */
  load(onProgress?: (message: string) => void): Promise<void>;
  run(code: string, stdin?: StdinProvider): Promise<RunResult>;
  stop(): Promise<void>;
  dispose(): void;

  /**
   * Set by the host to hear that a running program has paused to ask something.
   *
   * Only the Python runner does this, and only when the page is cross-origin-isolated.
   * Its absence is how the UI knows to fall back to answers supplied before the run.
   */
  onAsk?: ((prompt: string) => void) | null;
  /** Hand the waiting program an answer. Present only alongside `onAsk`. */
  answerInput?(text: string): void;
  /** Whether this runner will pause and ask, rather than needing answers up front. */
  readonly asksInteractively?: boolean;

  /**
   * True when the runner draws, and the editor panel has to give it somewhere to
   * draw into. The panel supplies the element; what goes in it is the runner's
   * business — p5 puts an iframe there.
   */
  readonly needsSurface?: boolean;
  /** Hand over that element, or null when it goes away. Present with `needsSurface`. */
  attachSurface?(surface: HTMLElement | null): void;
  /**
   * Set by the host to hear about a crash that happens after a run was reported —
   * a `draw()` that throws on frame 200, which no one-shot result can catch.
   */
  onLateError?: ((message: string) => void) | null;
}

/** Output beyond this is truncated before it reaches the tutor's context.
 *
 * A `for i in range(100000): print(i)` is a normal thing for a learner to write,
 * and shipping all of it into the prompt would cost more than the whole session.
 * The backend caps `detail` at 8000 characters; this stays well under. */
export const MAX_DETAIL_CHARS = 4000;

export function truncateDetail(text: string, limit = MAX_DETAIL_CHARS): string {
  if (text.length <= limit) return text;
  const kept = text.slice(0, limit);
  const dropped = text.length - limit;
  return `${kept}\n… ${dropped.toLocaleString()} more characters truncated`;
}

/** Classify a completed, error-free run. */
export function outcomeForOutput(output: string): RunOutcome {
  return output.trim() ? "ok" : "silent";
}
